import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader, StatusBadge, formatDate } from "@/lib/ui";
import { Users, ListChecks, CalendarDays, Wrench, FileSignature, Receipt, FileText, FolderOpen, Download, Search, ChevronRight } from "lucide-react";
import { saveOrShareBlob, fetchAndSave } from "@/lib/downloads";

const REPORTS = [
  { key: "clients",          label: "Clients",          icon: Users },
  { key: "tasks",            label: "Tasks",            icon: ListChecks },
  { key: "appointments",     label: "Appointments",     icon: CalendarDays },
  { key: "service_requests", label: "Service Requests", icon: Wrench },
  { key: "contracts",        label: "Contracts",        icon: FileSignature },
  { key: "invoices",         label: "Invoices",         icon: Receipt },
  { key: "forms",            label: "Form Responses",   icon: FileText },
  { key: "documents",        label: "Documents",        icon: FolderOpen },
];

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); };
const money = (cents, currency = "USD") => new Intl.NumberFormat(undefined, { style: "currency", currency }).format((cents || 0) / 100);

export default function Reports() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const active   = params.get("type") || "clients";
  const q        = params.get("q") || "";
  const from     = params.get("from") || monthAgo();
  const to       = params.get("to") || today();
  const status   = params.get("status") || "all";
  const clientId = params.get("client_id") || "all";

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === "all") next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next, { replace: true });
  };

  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Client dropdown — load once
  useEffect(() => {
    if (!currentOrgId) return;
    supabase.from("clients").select("id, first_name, last_name")
      .eq("organization_id", currentOrgId).order("first_name")
      .then(({ data }) => setClients(data || []));
  }, [currentOrgId]);

  const load = async () => {
    if (!currentOrgId) return;
    setLoading(true);
    let data = [];
    try {
      const cid = clientId !== "all" ? clientId : null;
      if (active === "clients") {
        let q1 = supabase.from("clients")
          .select("id, first_name, last_name, email, phone, status, referral_source, intake_date, created_at")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "tasks") {
        let q1 = supabase.from("tasks")
          .select("id, title, status, priority, due_date, completed_at, created_at, client_id, clients(first_name, last_name), profiles:assigned_to(full_name)")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "appointments") {
        // Prefer bookings (new Scheduling module); fall back to appointments (legacy)
        let q1 = supabase.from("bookings")
          .select("id, starts_at, ends_at, status, duration_minutes, location_type, client_first_name, client_last_name, client_id, appointment_type_snapshot")
          .eq("organization_id", currentOrgId)
          .gte("starts_at", from + "T00:00:00").lte("starts_at", to + "T23:59:59")
          .order("starts_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "service_requests") {
        let q1 = supabase.from("service_requests")
          .select("id, request_number, title, status, priority, requested_due_date, created_at, client_id, clients(first_name, last_name)")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "contracts") {
        let q1 = supabase.from("service_contracts")
          .select("id, contract_number, contract_name, status, effective_date, end_date, created_at, client_id, clients(first_name, last_name), finalized_pdf_url")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "invoices") {
        let q1 = supabase.from("invoices")
          .select("id, invoice_number, status, total_cents, currency, due_date, issued_at, created_at, client_id, clients(first_name, last_name), finalized_pdf_url")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "forms") {
        let q1 = supabase.from("form_responses")
          .select("id, form_template_id, submitted_at, client_id, clients(first_name, last_name), form_templates(name)")
          .eq("organization_id", currentOrgId)
          .gte("submitted_at", from + "T00:00:00").lte("submitted_at", to + "T23:59:59")
          .order("submitted_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      } else if (active === "documents") {
        let q1 = supabase.from("documents")
          .select("id, name, document_type, size_bytes, storage_path, created_at, client_id, clients(first_name, last_name), profiles:uploaded_by(full_name)")
          .eq("organization_id", currentOrgId)
          .gte("created_at", from + "T00:00:00").lte("created_at", to + "T23:59:59")
          .order("created_at", { ascending: false });
        if (cid) q1 = q1.eq("client_id", cid);
        const { data: d, error } = await q1;
        if (error) throw error;
        data = d || [];
      }
    } catch (e) {
      toast.error(e?.message || "Could not load report");
    }
    setRows(data);
    setLoading(false);
  };
  // Reload on filter change
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, currentOrgId, from, to, clientId]);

  const statusOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.status).filter(Boolean))), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    const client = r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : `${r.client_first_name || ""} ${r.client_last_name || ""}`;
    const staff = r.profiles?.full_name || "";
    const searchIn = [
      r.title, r.first_name, r.last_name, r.email, r.phone,
      r.invoice_number, r.contract_number, r.request_number, r.contract_name,
      r.name, r.category, r.form_templates?.title,
      client, staff, r.appointment_type_snapshot?.name, r.referral_source,
    ].filter(Boolean).join(" ").toLowerCase();
    return searchIn.includes(ql);
  }), [rows, q, status]);

  const total = useMemo(() => {
    if (active === "invoices") {
      const sum = filtered.reduce((s, r) => s + (r.total_cents || 0), 0);
      const cur = filtered[0]?.currency || "USD";
      return { label: "Total billed", value: money(sum, cur) };
    }
    return { label: "Total records", value: filtered.length };
  }, [filtered, active]);

  const view = ROW_ACTIONS[active];

  const openRow = async (r) => {
    if (!view) return;
    if (typeof view.open === "function") {
      const dest = await view.open(r);
      if (dest) navigate(dest);
    }
  };

  const cols = COLUMNS[active] || [];

  const exportCsv = async () => {
    if (filtered.length === 0) return toast.error("Nothing to export");
    const header = cols.map((c) => c.label).join(",");
    const body = filtered.map((r) => cols.map((c) => `"${String(c.get(r) ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [header, ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    try {
      await saveOrShareBlob(blob, `clientdesk-${active}-${from}-to-${to}.csv`,
        { title: `ClientDesk VA — ${active} report`, dialogTitle: "Save or share report" });
      toast.success(`Exported ${filtered.length} rows`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Search and open any record in your workspace. Every row is clickable." />

      {/* Category selector */}
      <div className="flex gap-2 flex-wrap" data-testid="report-categories">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const isOn = active === r.key;
          return (
            <button key={r.key} data-testid={`report-tab-${r.key}`}
              onClick={() => setParam({ type: r.key, status: null, q: null })}
              className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 border transition-colors ${
                isOn ? "bg-[#E8712C] text-white border-[#E8712C]" : "bg-white border-[#E8E2D9] text-[#57534E] hover:text-[#1C1917]"
              }`}>
              <Icon size={14} strokeWidth={1.75} /> {r.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4 grid grid-cols-1 md:grid-cols-[1fr_180px_150px_150px_150px_auto] gap-3 items-end"
           data-testid="report-filters">
        <div>
          <Label className="text-xs">Search</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <Input value={q} onChange={(e) => setParam({ q: e.target.value })}
                   placeholder="Search records…" className="pl-9" data-testid="report-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Client</Label>
          <Select value={clientId} onValueChange={(v) => setParam({ client_id: v })}>
            <SelectTrigger className="mt-1" data-testid="report-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setParam({ from: e.target.value })} className="mt-1" data-testid="report-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setParam({ to: e.target.value })} className="mt-1" data-testid="report-to" />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={(v) => setParam({ status: v })}>
            <SelectTrigger className="mt-1" data-testid="report-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {statusOptions.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="report-export-btn" onClick={exportCsv}
                className="rounded-full bg-[#1C1917] hover:bg-[#292524]">
          <Download size={13} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Summary */}
      <div className="text-sm text-[#57534E]">
        Showing <b className="text-[#1C1917]">{filtered.length}</b> of {rows.length} · <b className="text-[#1C1917]">{total.label}: {total.value}</b>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E2D9]" data-testid="report-empty">
          <p className="text-sm">No records match your filters.</p>
          <p className="text-xs text-[#A8A29E] mt-1">Try widening the date range or clearing the client filter.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                {cols.map((c) => <th key={c.label} className="text-left px-5 py-3">{c.label}</th>)}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`report-row-${r.id}`}
                    onClick={() => openRow(r)}
                    className="cursor-pointer hover:bg-[#FAF8F5] transition-colors">
                  {cols.map((c) => <td key={c.label} className="px-5 py-3 align-top">{c.render ? c.render(r) : (c.get(r) ?? "—")}</td>)}
                  <td className="px-3"><ChevronRight size={14} className="text-[#A8A29E]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Row-click destinations for each report category.
const ROW_ACTIONS = {
  clients:          { open: (r) => `/app/clients/${r.id}` },
  tasks:            { open: (r) => `/app/tasks?id=${r.id}` },
  appointments:     { open: (r) => `/app/scheduling?tab=bookings&id=${r.id}` },
  service_requests: { open: (r) => `/app/service-requests?id=${r.id}` },
  contracts:        { open: (r) => `/app/contracts?id=${r.id}` },
  invoices:         { open: (r) => `/app/invoices?id=${r.id}` },
  forms:            { open: (r) => `/app/forms/${r.form_template_id}/responses` },
  documents:        { open: (r) => `/app/documents?id=${r.id}` },
};

// Column definitions per report — each has a label, a getter (used for search & CSV),
// and an optional render for the table.
const COLUMNS = {
  clients: [
    { label: "Name", get: (r) => `${r.first_name} ${r.last_name}` },
    { label: "Email", get: (r) => r.email || "" },
    { label: "Phone", get: (r) => r.phone || "" },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
    { label: "Referral", get: (r) => r.referral_source || "" },
    { label: "Intake date", get: (r) => r.intake_date || "", render: (r) => r.intake_date ? formatDate(r.intake_date) : "—" },
    { label: "Created", get: (r) => r.created_at, render: (r) => formatDate(r.created_at) },
  ],
  tasks: [
    { label: "Task", get: (r) => r.title },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
    { label: "Assigned", get: (r) => r.profiles?.full_name || "" },
    { label: "Priority", get: (r) => r.priority, render: (r) => <StatusBadge value={r.priority} /> },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
    { label: "Due", get: (r) => r.due_date || "", render: (r) => r.due_date ? formatDate(r.due_date) : "—" },
    { label: "Completed", get: (r) => r.completed_at || "", render: (r) => r.completed_at ? formatDate(r.completed_at) : "—" },
  ],
  appointments: [
    { label: "When", get: (r) => r.starts_at, render: (r) => new Date(r.starts_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) },
    { label: "Type", get: (r) => r.appointment_type_snapshot?.name || "" },
    { label: "Client", get: (r) => r.client_first_name ? `${r.client_first_name} ${r.client_last_name || ""}` : "" },
    { label: "Duration", get: (r) => `${r.duration_minutes || 0} min` },
    { label: "Location", get: (r) => (r.location_type || "").replace(/_/g, " "), render: (r) => <span className="capitalize">{(r.location_type || "—").replace(/_/g, " ")}</span> },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
  ],
  service_requests: [
    { label: "Number", get: (r) => r.request_number || "" },
    { label: "Title", get: (r) => r.title },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
    { label: "Assigned", get: (r) => r.profiles?.full_name || "" },
    { label: "Priority", get: (r) => r.priority, render: (r) => <StatusBadge value={r.priority} /> },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
    { label: "Due", get: (r) => r.requested_due_date || "", render: (r) => r.requested_due_date ? formatDate(r.requested_due_date) : "—" },
  ],
  contracts: [
    { label: "Number", get: (r) => r.contract_number || "" },
    { label: "Contract", get: (r) => r.contract_name || "" },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
    { label: "Effective", get: (r) => r.effective_date || "", render: (r) => r.effective_date ? formatDate(r.effective_date) : "—" },
    { label: "End", get: (r) => r.end_date || "", render: (r) => r.end_date ? formatDate(r.end_date) : "—" },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
  ],
  invoices: [
    { label: "Number", get: (r) => r.invoice_number || "" },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
    { label: "Issued", get: (r) => r.issued_at || "", render: (r) => r.issued_at ? formatDate(r.issued_at) : "—" },
    { label: "Due", get: (r) => r.due_date || "", render: (r) => r.due_date ? formatDate(r.due_date) : "—" },
    { label: "Total", get: (r) => (r.total_cents || 0) / 100, render: (r) => <b>{money(r.total_cents, r.currency)}</b> },
    { label: "Status", get: (r) => r.status, render: (r) => <StatusBadge value={r.status} /> },
  ],
  forms: [
    { label: "Form", get: (r) => r.form_templates?.name || "" },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "Anonymous" },
    { label: "Submitted", get: (r) => r.submitted_at || "", render: (r) => r.submitted_at ? formatDate(r.submitted_at) : "—" },
  ],
  documents: [
    { label: "Name", get: (r) => r.name || "" },
    { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "Workspace" },
    { label: "Type", get: (r) => r.document_type || "", render: (r) => <span className="capitalize">{(r.document_type || "—").replace(/_/g, " ")}</span> },
    { label: "Uploaded by", get: (r) => r.profiles?.full_name || "" },
    { label: "Uploaded", get: (r) => r.created_at, render: (r) => formatDate(r.created_at) },
  ],
};
